import * as Sentry from '@sentry/react'

export { scrubSentryEvent } from '@cortado/observability'

export function newRequestId(): string {
  return globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

export function apiTraceTargets(apiUrl?: string): Array<string | RegExp> {
  const targets: Array<string | RegExp> = [
    'localhost',
    /^https:\/\/(?:[^/]+\.)?cortado\.pro/,
  ]
  if (apiUrl) {
    try {
      targets.push(new URL(apiUrl).origin)
    } catch {
      targets.push(apiUrl)
    }
  }
  return targets
}

export function recordResponseCorrelation(
  headers: Headers,
  span?: { setAttribute(key: string, value: string): unknown }
): { requestId?: string; traceId?: string } {
  const requestId = headers.get('X-Request-ID') || undefined
  const traceId = headers.get('X-Trace-ID') || undefined
  if (requestId) span?.setAttribute('server.request_id', requestId)
  if (traceId) span?.setAttribute('sentry.trace_id', traceId)
  if (requestId || traceId) {
    Sentry.addBreadcrumb({
      category: 'api.correlation',
      message: 'Backend response correlation',
      level: 'info',
      data: { requestId, traceId },
    })
  }
  return { requestId, traceId }
}

export async function tracedFetch(
  operation: string,
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const headers = new Headers(init.headers)
  const requestId = headers.get('X-Request-ID') || newRequestId()
  headers.set('X-Request-ID', requestId)

  return Sentry.startSpan(
    {
      name: operation,
      op: 'http.client',
      attributes: {
        'http.request_id': requestId,
        'http.request.method': init.method || 'GET',
      },
    },
    async (span) => {
      try {
        const response = await fetch(input, { ...init, headers })
        span.setAttribute('http.response.status_code', response.status)
        recordResponseCorrelation(response.headers, span)
        span.setStatus(
          response.ok
            ? { code: 1 }
            : { code: 2, message: httpStatusName(response.status) }
        )
        return response
      } catch (error) {
        span.setStatus({ code: 2, message: 'unavailable' })
        throw error
      }
    }
  )
}

function httpStatusName(status: number): string {
  if (status === 401) return 'unauthenticated'
  if (status === 403) return 'permission_denied'
  if (status === 404) return 'not_found'
  if (status === 429) return 'resource_exhausted'
  if (status >= 500) return 'internal_error'
  return 'invalid_argument'
}
